import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, Crown } from "lucide-react";
import UserAvatar from "./league/UserAvatar";
import { teamLogoUrl } from "../utils/teamLogo";
import { signed } from "../utils/leagueCard";
import { LEAGUE_STATUS } from "../utils/leagueStatus";
import { PLAYOFF_BYES } from "../utils/season";
import { CARD_LABEL, META_SIZE, MIN_TEXT, ROW_SIZE } from "../utils/homeType";

// One league on /home. A dark header with the name and where the viewer
// stands, and the two buttons along the bottom, in every state.
//
// Once a league is scoring, the middle is the week: one headline at a time
// from the league's news, the viewer's five starters with the open fixture
// under them, and a five-row table. A manager with a 5x Play live carries a
// gold ring on their avatar in that table.
//
// Before scoring starts, the header's second line says where the draft is up
// to and there is no middle.

// Gold rather than --color-tripplay, which is the purple the Lineup tab uses
// for the 5x Play: here it has to stand out on any avatar, and gold was the
// choice for this card.
const FIVE_GOLD = "#f59e0b";

// inView: this is the card the rail is showing. Only that card carries the
// tour names for its news, table and lineup, so the "?" beside Your Leagues
// lights up the league on screen rather than the first in the list. It also
// keeps the headlines of cards off screen from turning over.
function LeagueCard({ league, status, theme, inView = true }) {
  const navigate = useNavigate();
  const drafted = league.draft_complete;
  const live = status.key === LEAGUE_STATUS.live && league.card;
  const points = league.standing?.points;
  const subline = live && points != null
    ? <><strong style={{ color: "#fff", fontWeight: 800 }}>{points} pts</strong>{league.card.week ? ` · Week ${league.card.week}` : ""}</>
    : status.detail;

  return (
    <div
      data-tour="home-league"
      className="league-card"
      style={{ backgroundColor: "var(--color-bg-card)", border: "1px solid var(--color-border)", borderRadius: 16, overflow: "hidden", display: "flex", flexDirection: "column" }}
    >
      {/* Header: the name and a second line on the dark field, the state in
          the league's colour on the right behind a slanted edge. The colour
          washes into the dark field from the top left and toward the seam,
          and the panel fades from bright at the top to dark at the foot, so
          the two read as one lit surface rather than two flat blocks. The
          8-digit hexes are the theme colour with alpha. Both sides
          are min-width 0 with ellipsis, so a long name and a long state each
          give way rather than push the other out of the card. */}
      <div
        onClick={() => navigate(drafted ? `/${league.id}/lineup` : `/${league.id}/draft-room`)}
        style={{ position: "relative", minHeight: 60, flexShrink: 0, overflow: "hidden", cursor: "pointer", display: "flex", alignItems: "stretch", background: [
          `radial-gradient(90% 160% at 0% 0%, ${theme.rightBase}59, transparent 65%)`,
          `linear-gradient(90deg, transparent 45%, ${theme.rightBase}4d)`,
          `linear-gradient(180deg, ${theme.leftLift}, ${theme.leftBase})`,
        ].join(", "), borderBottom: "1px solid rgba(255,255,255,0.12)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)" }}
      >
        <div style={{ flex: "1 1 auto", minWidth: 0, padding: "10px 12px 10px 16px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#fff", letterSpacing: "-0.01em", lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {league.name || "Unnamed League"}
          </div>
          {subline && (
            <div style={{ marginTop: 2, fontSize: 12, fontWeight: 500, color: "rgba(255,255,255,0.75)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {subline}
            </div>
          )}
        </div>
        <div style={{ position: "relative", flexShrink: 0, maxWidth: "46%", display: "flex", alignItems: "center", padding: "0 16px 0 24px" }}>
          <div aria-hidden="true" style={{ position: "absolute", inset: "0 -24px 0 0", transform: "skewX(-16deg)", transformOrigin: "bottom left", background: [
            `radial-gradient(80% 90% at 70% 0%, rgba(255,255,255,0.14), transparent 70%)`,
            `linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.3))`,
            `linear-gradient(180deg, ${theme.rightLift}, ${theme.rightBase})`,
          ].join(", ") }} />
          <span style={{ position: "relative", padding: "4px 9px", borderRadius: 8, backgroundColor: "rgba(255,255,255,0.06)", fontSize: 14, fontWeight: 800, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {status.label}
          </span>
        </div>
      </div>

      {live && (
        <>
          {/* Only when there is news: a quiet week shows no bar at all. */}
          {league.card.ticker.length > 0 && <NewsBar items={league.card.ticker} active={inView} tour={inView ? "home-league-news" : undefined} />}
          {league.card.myLineup && <LineupStrip leagueId={league.id} lineup={league.card.myLineup} tour={inView ? "home-league-lineup" : undefined} />}
          <LiveTable leagueId={league.id} table={league.card.table} tour={inView ? "home-league-table" : undefined} />
        </>
      )}

      {/* The pair changes with draft state, but the card keeps the same
          shape either way, so the list does not shift as leagues finish
          drafting. */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
        <button
          onClick={() => navigate(drafted ? `/${league.id}/lineup` : `/${league.id}/scouting`)}
          style={{ ...buttonStyle, borderRight: "1px solid var(--color-border)" }}
        >
          {drafted ? "My Lineup" : "Scouting"}
        </button>
        <button onClick={() => navigate(`/${league.id}/league`)} style={buttonStyle}>
          {drafted ? "Standings" : "League"}
        </button>
      </div>
    </div>
  );
}

const buttonStyle = {
  display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "13px 0",
  backgroundColor: "var(--color-bg-card)", border: "none", borderTop: "1px solid var(--color-border)",
  color: "var(--color-brand)", fontWeight: 700, fontSize: 14, cursor: "pointer",
};

const sectionLabel = CARD_LABEL;

// League news, one headline at a time behind a red BREAKING plate, fading to
// the next every few seconds. A tap moves on early. It holds still on a card
// the rail is not showing, and with reduced motion asked for the change is a
// cut rather than a fade.
const RED = "#d71920";
const GREEN = "var(--color-success)";
const HEADLINE_MS = 2500;

function NewsBar({ items, active, tour }) {
  const [index, setIndex] = useState(0);
  const count = items.length;
  // A refresh can shorten the list under the current index.
  const shown = index % count;
  const item = items[shown];

  useEffect(() => {
    if (!active || count < 2) return undefined;
    const timer = setTimeout(() => setIndex((i) => (i + 1) % count), HEADLINE_MS);
    return () => clearTimeout(timer);
  }, [active, count, index]);

  return (
    <button
      type="button"
      data-tour={tour}
      onClick={() => count > 1 && setIndex((shown + 1) % count)}
      aria-label={count > 1 ? `Headline ${shown + 1} of ${count}. Tap for the next.` : undefined}
      style={{
        display: "flex", alignItems: "center", gap: 10, width: "100%", minHeight: 58, padding: "9px 14px", textAlign: "left",
        backgroundColor: "var(--league-news-bg)", border: "none", borderBottom: "1px solid var(--color-border)", font: "inherit", color: "inherit",
        cursor: count > 1 ? "pointer" : "default", WebkitTapHighlightColor: "transparent", WebkitAppearance: "none", appearance: "none",
      }}
    >
      <span style={{ flexShrink: 0, padding: "5px 8px", borderRadius: 4, backgroundColor: RED, boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.35)", color: "#fff", fontSize: 11, fontWeight: 900, fontStyle: "italic", letterSpacing: "0.06em" }}>
        BREAKING
      </span>
      <span key={shown} className="league-news-item" style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ flex: 1, minWidth: 0, fontSize: ROW_SIZE, fontWeight: 700, lineHeight: 1.25, color: "var(--color-text-strong)", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {item.kind === "tripPlay" && <><FiveChip inline /><span className="sr-only">5x Play: </span></>}
          {item.kind === "up" && <span aria-hidden="true" style={{ color: GREEN, fontSize: 11 }}>▲ </span>}
          {item.kind === "down" && <span aria-hidden="true" style={{ color: "var(--color-danger)", fontSize: 11 }}>▼ </span>}
          {item.text}
          {item.detail && <span style={{ color: item.tone === "neg" ? "var(--color-danger)" : GREEN, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}> {item.detail}</span>}
        </span>
        {item.when && <span style={{ flexShrink: 0, fontSize: META_SIZE, fontWeight: 500, color: "var(--color-text-muted)" }}>{item.when}</span>}
      </span>
      <style>{`
        .league-news-item { animation: league-news-in 0.35s ease; }
        @keyframes league-news-in { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
        @media (prefers-reduced-motion: reduce) { .league-news-item { animation: none; } }
      `}</style>
    </button>
  );
}


// inline: sitting in a line of text, where an inline-flex box lines up its
// own small "5x" with the text's baseline and so hangs low. Centred on the
// line instead, at the text's own height.
function FiveChip({ small, inline }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", verticalAlign: inline ? "middle" : undefined, position: inline ? "relative" : undefined, top: inline ? -1 : undefined, marginRight: inline ? 7 : undefined, height: small ? 13 : 16, padding: small ? "0 3px" : "0 5px", borderRadius: 4, backgroundColor: FIVE_GOLD, color: "#111", fontSize: small ? 8.5 : 10, fontWeight: 900, letterSpacing: "0.02em", lineHeight: 1 }}>
      5x
    </span>
  );
}

// The top three and the viewer (trimTable), six rows. A break marks places
// skipped to reach the viewer, whose row carries the brand edge, and the
// playoff line sits under sixth when sixth is shown. Each row
// opens that manager's card in the Scoreboard carousel, the same jump the
// standings rows make; View all is the full table.
function LiveTable({ leagueId, table, tour }) {
  return (
    <div data-tour={tour} style={{ borderTop: "1px solid var(--color-border)", padding: "12px 0 4px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "0 14px 6px" }}>
        <span style={sectionLabel}>Standings</span>
        <Link to={`/${leagueId}/league`} className="no-underline" style={{ fontSize: 13, fontWeight: 700, color: "var(--color-brand)" }}>View all</Link>
      </div>
      {table.rows.map((row, i) => (
        <React.Fragment key={row.memberId}>
          <Link
            to={`/${leagueId}/scoreboard?member=${row.memberId}`}
            className="no-underline"
            style={{
              display: "grid", gridTemplateColumns: "18px 28px 1fr auto", alignItems: "center", gap: 10,
              padding: "6px 14px 6px 11px", fontSize: ROW_SIZE, color: "var(--color-text-strong)",
              borderLeft: `3px solid ${row.isMe ? "var(--color-brand)" : "transparent"}`,
              backgroundColor: row.isMe ? "var(--color-band-bg)" : "transparent",
            }}
          >
            {/* Seeds 1 and 2 carry BYE under the number, as on the league's
                standings: they skip the playoffs' opening round. */}
            <span style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "var(--color-text-muted)", fontVariantNumeric: "tabular-nums" }}>{row.rank}</span>
              {row.rank <= PLAYOFF_BYES && (
                <span aria-label="Playoff bye" style={{ marginTop: 1, fontSize: MIN_TEXT, fontWeight: 900, lineHeight: 1, letterSpacing: "0.02em", color: "var(--color-success-text)" }}>BYE</span>
              )}
            </span>
            <span style={{ position: "relative", display: "inline-flex", width: 26, height: 26 }}>
              <span style={{ borderRadius: "50%", display: "inline-flex", boxShadow: row.tripPlay ? `0 0 0 2px ${FIVE_GOLD}` : "none" }}>
                <UserAvatar member={{ avatar_url: row.avatarUrl, firstName: row.firstName }} size={26} />
              </span>
              {row.tripPlay && (
                <span style={{ position: "absolute", right: -8, bottom: -5, border: "1.5px solid var(--color-bg-card)", borderRadius: 5, display: "inline-flex" }}>
                  <FiveChip small />
                </span>
              )}
            </span>
            <span style={{ minWidth: 0, fontWeight: row.isMe ? 700 : 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.teamName}</span>
            <span style={{ fontWeight: 800, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{row.total}</span>
          </Link>
          {i === table.cutAfter && <PlayoffLine />}
          {i === table.gapAfter && (
            <div aria-label="Places skipped" style={{ textAlign: "center", fontSize: 14, lineHeight: "10px", letterSpacing: "0.2em", color: "var(--color-text-faint)", padding: "2px 0 4px" }}>···</div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// The same red pill as the league's own standings, between two hairlines.
function PlayoffLine() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "3px 14px" }}>
      <span style={{ flex: 1, height: 1, backgroundColor: "var(--color-border)" }} />
      <span style={{ flexShrink: 0, padding: "2px 9px", borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", backgroundColor: "var(--color-danger-bg)", color: "var(--color-danger-fill)", border: "1px solid var(--color-danger-border)" }}>
        Playoff line
      </span>
      <span style={{ flex: 1, height: 1, backgroundColor: "var(--color-border)" }} />
    </div>
  );
}

const LOGO = 38;

// The viewer's five starting slots, between the news and the table. Logo, points so far (multipliers included, so the
// five add up to the week total), and a short game state. The captain carries
// the app's crown in its captain colour; a 5x Play carries the gold chip
// instead, since it always sits on the captain.
//
// One slot is always picked, its fixture open in a full-width line under the
// strip and its slot shaded. It starts on the most useful one (defaultPick:
// a live game, else the next kickoff, else the captain); tapping another
// moves the pick. The fixture line and the My Lineup button below both lead
// into the Lineup tab.
function LineupStrip({ leagueId, lineup, tour }) {
  const [chosen, setChosen] = useState(null);
  // A choice holds until the slot it points at empties, which a poll can do
  // if the lineup changes elsewhere.
  const picked = chosen !== null && lineup.slots[chosen]?.slug ? chosen : lineup.defaultPick;
  const open = picked === null ? null : lineup.slots[picked];

  return (
    <div data-tour={tour} style={{ padding: "12px 14px 12px" }}>
      <div style={{ ...sectionLabel, display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <span>Your lineup</span>
        <span>{signed(lineup.weekPoints)} this week</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${lineup.slots.length}, 1fr)`, gap: 6 }}>
        {lineup.slots.map((slot, i) => {
          const isPicked = open && picked === i;
          return (
            <button
              key={slot.slug || `empty-${i}`}
              type="button"
              disabled={!slot.slug}
              aria-pressed={slot.slug ? Boolean(isPicked) : undefined}
              aria-label={slot.slug ? `${slot.school} fixture` : "Empty slot"}
              onClick={() => setChosen(i)}
              style={{
                minWidth: 0, padding: 0, border: "none", background: "none", font: "inherit", color: "inherit",
                WebkitAppearance: "none", appearance: "none", WebkitTapHighlightColor: "transparent",
                cursor: slot.slug ? "pointer" : "default",
              }}
            >
              {/* The highlight lives on this box, not the button,
                  with no transition, and the key remounts it whenever its
                  picked state flips. Safari on iOS kept painting earlier
                  picks as highlighted when the button itself carried them. */}
              <div
                key={isPicked ? "picked" : "rest"}
                style={{
                  textAlign: "center", padding: "6px 0 4px", borderRadius: 10,
                  backgroundColor: isPicked ? "var(--color-bg-subtle)" : "transparent",
                }}
              >
              <div style={{ position: "relative", width: LOGO, height: LOGO, margin: "0 auto 4px" }}>
                {slot.slug ? (
                  <SlotLogo school={slot.school} size={LOGO} />
                ) : (
                  <div style={{ width: LOGO, height: LOGO, borderRadius: "50%", border: "1.5px dashed var(--color-text-faint)", boxSizing: "border-box" }} />
                )}
                {slot.live && (
                  <span aria-hidden="true" style={{ position: "absolute", top: -2, left: -3, width: 8, height: 8, borderRadius: "50%", backgroundColor: "#ef4444", border: "1.5px solid var(--color-bg-card)" }} />
                )}
                {slot.tripPlay ? (
                  <span style={{ position: "absolute", top: -6, right: -10, border: "1.5px solid var(--color-bg-card)", borderRadius: 5, display: "inline-flex" }}>
                    <FiveChip small />
                  </span>
                ) : slot.captain && (
                  <span aria-label="Captain" style={{ position: "absolute", top: -6, right: -8, width: 17, height: 17, borderRadius: "50%", backgroundColor: "var(--color-bg-card)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Crown size={12} strokeWidth={2.5} fill="var(--color-captain)" style={{ color: "var(--color-captain)" }} />
                  </span>
                )}
              </div>
              <div style={{ fontSize: ROW_SIZE, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: slot.points > 0 ? "var(--color-success)" : slot.points < 0 ? "var(--color-danger)" : "var(--color-text-strong)" }}>
                {slot.points == null ? "\u2013" : signed(slot.points)}
              </div>
              <div style={{ fontSize: META_SIZE, color: slot.live ? "var(--color-danger)" : "var(--color-text-muted)", fontWeight: slot.live ? 700 : 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {slot.slug ? slot.state : "Empty"}
              </div>
              </div>
            </button>
          );
        })}
      </div>
      {open && <FixtureLine leagueId={leagueId} slot={open} />}
    </div>
  );
}

// The picked team's fixture, full width: that team's logo and name, which the
// strip itself never spells out, then the opponent with their record, when or
// how it is going, and the spread. The chevron says it leads somewhere: the
// Lineup tab, where the game sits beside the rest of the week.
function FixtureLine({ leagueId, slot }) {
  const { fixture } = slot;
  const parts = fixture.when !== undefined
    ? [fixture.when, fixture.spread && `Spread ${fixture.spread}`].filter(Boolean)
    : [fixture.detail];
  return (
    <Link
      to={`/${leagueId}/lineup`}
      className="no-underline"
      style={{ marginTop: 10, padding: "10px 12px", borderRadius: 12, backgroundColor: "var(--color-bg-subtle)", display: "flex", alignItems: "center", gap: 10, color: "var(--color-text-strong)" }}
    >
      <SlotLogo key={slot.slug} school={slot.school} size={32} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: ROW_SIZE, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {/* Your team in full strength; the opponent, with its record,
              set back in the muted style so the two cannot be confused. */}
          <span style={{ fontWeight: 800 }}>{slot.school}</span>
          {fixture.opponent && (
            <span style={{ fontWeight: 500, color: "var(--color-text-muted)" }}>
              {" "}{fixture.title}
              {fixture.record && ` (${fixture.record})`}
            </span>
          )}
        </div>
        <div style={{ fontSize: META_SIZE, color: "var(--color-text-muted)", marginTop: 2 }}>
          {parts.map((part, i) => (
            <React.Fragment key={i}>
              {i > 0 && " \u00b7 "}
              <span style={i === 0 && fixture.live ? { color: "var(--color-danger)", fontWeight: 700 } : undefined}>{part}</span>
            </React.Fragment>
          ))}
        </div>
      </div>
      <ChevronRight size={18} strokeWidth={2.5} style={{ flexShrink: 0, color: "var(--color-text-muted)" }} />
    </Link>
  );
}

// A team with no logo file falls back to its initials rather than the
// browser's broken-image box. That is any team outside the FBS map, whose
// slug is all there is to go on (an FCS team on a roster, for one).
function SlotLogo({ school, size = 30 }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    const initials = school
      .split(/[\s-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w.charAt(0).toUpperCase())
      .join("");
    return (
      <div
        aria-label={school}
        style={{
          width: size, height: size, borderRadius: "50%", boxSizing: "border-box",
          display: "flex", alignItems: "center", justifyContent: "center",
          backgroundColor: "var(--color-bg-subtle)", color: "var(--color-text-muted)",
          fontSize: 11, fontWeight: 800,
        }}
      >
        {initials}
      </div>
    );
  }
  return (
    <img
      src={teamLogoUrl(school)}
      alt={school}
      onError={() => setFailed(true)}
      style={{ width: size, height: size, objectFit: "contain", display: "block" }}
    />
  );
}

// Memoized: the rail re-renders on every scroll frame of a swipe, and the
// cards it passes are stable between leagues refreshes.
export default React.memo(LeagueCard);
